import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Badge } from "@/shared/components/ui/badge";
import { Loader2, AlertCircle } from "lucide-react";
import { formatCPF } from "@/shared/lib/formatters";
import type { RentalResponseDTO } from "@/features/rentals/types/rental.types";
import type { SellerResponseDTO } from "../types/seller.types";

interface SellerRentalsModalProps {
  open: boolean;
  seller: SellerResponseDTO;
  rentals: RentalResponseDTO[];
  rentalsLoading: boolean;
  rentalsError: boolean;
  rentalsTotal: number;
  onClose: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  ACTIVE: {
    label: "Ativo",
    className: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  },
  RENTED: {
    label: "Alugado",
    className: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  },
  IN_PROGRESS: {
    label: "Em andamento",
    className: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  },
  INCOMPLETE: {
    label: "Incompleto",
    className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  },
  PENDING: {
    label: "Pendente",
    className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  },
  COMPLETED: {
    label: "Finalizado",
    className: "bg-green-500/20 text-green-400 border-green-500/30",
  },
  RETURNED: {
    label: "Devolvido",
    className: "bg-green-500/20 text-green-400 border-green-500/30",
  },
  OVERDUE: {
    label: "Atrasado",
    className: "bg-red-500/20 text-red-400 border-red-500/30",
  },
  LATE: {
    label: "Atrasado",
    className: "bg-red-500/20 text-red-400 border-red-500/30",
  },
  CANCELLED: {
    label: "Cancelado",
    className: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  },
  CANCELED: {
    label: "Cancelado",
    className: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  },
};

const formatDate = (date?: string) =>
  date ? new Date(date).toLocaleDateString("pt-BR") : "—";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

const formatDocument = (document: string) => {
  const raw = document.replace(/\D/g, "");
  return raw.length === 11 ? formatCPF(raw) : document;
};

const getStatusConfig = (status: string) =>
  STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  };

const getRentalCode = (rental: RentalResponseDTO) =>
  rental.contractNumber || `CT${String(rental.id).padStart(3, "0")}`;

const getItemCount = (rental: RentalResponseDTO) =>
  rental.stockIds?.length ??
  rental.gownItems?.reduce((acc, g) => acc + g.quantity, 0) ??
  0;

export const SellerRentalsModal = ({
  open,
  seller,
  rentals,
  rentalsLoading,
  rentalsError,
  rentalsTotal,
  onClose,
}: SellerRentalsModalProps) => {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Aluguéis do Vendedor</DialogTitle>
          <DialogDescription>
            Lista de todos os aluguéis realizados
          </DialogDescription>
        </DialogHeader>

        {rentalsLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Carregando aluguéis...
            </p>
          </div>
        ) : rentalsError ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <AlertCircle className="w-8 h-8 text-destructive" />
            <p className="text-sm text-muted-foreground text-center">
              Não foi possível carregar os aluguéis.
              <br />
              Feche o modal e tente novamente.
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-lg border bg-card p-4 shrink-0">
              <div>
                <p className="font-semibold text-base">{seller.name}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDocument(seller.document)}
                </p>
              </div>
              <div className="mt-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Total de Aluguéis no mês
                </p>
                <p className="font-semibold">{rentalsTotal}</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {rentals.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  Nenhum aluguel encontrado para este vendedor no mês atual
                </div>
              ) : (
                rentals.map((rental) => {
                  const code = getRentalCode(rental);
                  const startDate = rental.expectedOutAt || rental.rentedAt;
                  const endDate =
                    rental.expectedInAt || rental.inAt || undefined;
                  const itemCount = getItemCount(rental);
                  const statusConfig = getStatusConfig(rental.status);

                  return (
                    <div
                      key={rental.id}
                      className="rounded-lg border bg-card p-4"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-semibold text-sm">
                          {code}
                        </span>
                        <Badge
                          variant="outline"
                          className={statusConfig.className}
                        >
                          {statusConfig.label}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(startDate)} — {formatDate(endDate)} ·{" "}
                          {itemCount} {itemCount === 1 ? "peça" : "peças"}
                        </span>
                        <span className="font-semibold text-primary text-sm">
                          {formatCurrency(rental.totalValue)}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
